import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ArchiveTicket,
  ContractStatus,
  getArchiveTicketActionState,
  getContracts,
  getSelectedContract,
  getUnread,
  getUnreadTicketContractIds,
  ITicketResidentOverview,
  ITicketResidentOverviewHQ,
  LoadActiveDamageTicketsForSelectedContract,
  LoadActiveDamageTicketsForSelectedContractHQ,
  LoadMetersByContract,
  SelectContract,
  TicketingVersion,
  TicketIssueType,
  TicketsOverviewDamageBaseComponent,
} from '@resident-nx/shared';
import { combineLatest, map, take, tap } from 'rxjs';
import { SideSheetService } from '../../../../services';
import { CardsContainerHeaderWebComponent } from '../../../molecules/cards-container-header/cards-container-header.component';
import { Store } from '@ngrx/store';
import { ContractPickerComponent } from '@resident-nx/web/ui';
import { SortingMenuComponent } from '../../sorting-menu/sorting-menu.component';
import { TicketsWebComponent } from '../tickets/tickets.component';
import { TicketCreationPageWebComponent } from '../ticket-creation/ticket-creation-page.component';
import { TicketInformationNoticeComponent } from '../ticket-information-notice/ticket-information-notice.component';
import { ModalService } from '../../../molecules/modal/modal.service';
import { TicketArchiveModalWebComponent } from '../../../molecules/ticket/ticket-archive-modal/ticket-archive-modal.component';

@Component({
  standalone: true,
  selector: 'rs-web-tickets-overview-damage',
  imports: [
    ContractPickerComponent,
    CardsContainerHeaderWebComponent,
    TicketsWebComponent,
    AsyncPipe,
    SortingMenuComponent,
    TicketInformationNoticeComponent,
  ],
  templateUrl: './tickets-overview-damage.component.html',
  styleUrl: './tickets-overview-damage.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class TicketsOverviewDamageWebComponent
  extends TicketsOverviewDamageBaseComponent
  implements OnInit
{
  #destroyRef = inject(DestroyRef);
  #modalService = inject(ModalService);
  #store = inject(Store);

  #contracts = this.#store.selectSignal(getContracts);
  #selectedContract = this.#store.selectSignal(getSelectedContract);
  protected readonly contractOptions = computed(() =>
    this.#contracts().map(contract => ({
      ...contract,
      disabled: contract.status === ContractStatus.EXPIRED,
    }))
  );
  protected readonly selectedContractId = computed(() => this.#selectedContract()?.id);
  protected readonly hasSingleContract = computed(() => this.contractOptions().length === 1);
  #unread = this.#store.selectSignal(getUnread);
  protected readonly unreadContractIds = computed(() =>
    getUnreadTicketContractIds(this.contractOptions(), this.#unread()?.unreadTicketMessages)
  );

  public isArchiving$ = this.ticketsStore
    .select(getArchiveTicketActionState)
    .pipe(map(state => state?.pending === true));

  ngOnInit(): void {
    combineLatest([this.selectedContract$, this.selectedSorting$])
      .pipe(
        tap(([_, sort]) => {
          this.sort = sort;
          this.ticketingVersion$.pipe(take(1)).subscribe(version => {
            if (version === TicketingVersion.HIERARCHICAL) {
              this.ticketsStore.dispatch(
                LoadActiveDamageTicketsForSelectedContractHQ({ ...this.pagination, sort })
              );
            } else if (version === TicketingVersion.METADATA) {
              this.ticketsStore.dispatch(
                LoadActiveDamageTicketsForSelectedContract({ ...this.pagination, sort })
              );
            }
          });
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe();
  }

  #sideSheetService = inject(SideSheetService);

  public selectContract(id: string): void {
    if (!id || id === this.selectedContractId()) {
      return;
    }
    this.#store.dispatch(SelectContract({ id }));
    this.#store.dispatch(LoadMetersByContract());
  }

  public onArchiveTicket(ticket: ITicketResidentOverview | ITicketResidentOverviewHQ): void {
    const modalRef = this.#modalService.open(TicketArchiveModalWebComponent, { centered: true });
    modalRef.componentInstance.ticketName =
      ticket.title || ticket.category?.name || ticket.externalId || '';
    modalRef.onClose().subscribe(() => {
      this.ticketsStore.dispatch(
        ArchiveTicket({
          ticketId: ticket.id,
          ticketIssueType: ticket.issueType,
        })
      );
    });
  }

  public override createTicket() {
    this.#sideSheetService.open(TicketCreationPageWebComponent, {
      data: {
        type: TicketIssueType.PROPERTY_DAMAGE,
      },
    });
  }
}
